/**
 * Formatting utilities for the Financial Dashboard.
 *
 * All chart x-axis labels and tooltips must use formatFiscalYear().
 * India: year=2024 → "FY24"  (April 2023 – March 2024)
 * US:    year=2024 → "FY2024" (January – December 2024)
 */

import type { RegionConfig } from '../../../types/financial';

export function formatFiscalYear(year: number, region?: RegionConfig): string {
  if (region?.fiscal_year_format === 'april-march') {
    const shortYear = (year % 100).toString().padStart(2, '0');
    return `FY${shortYear}`;
  }
  if (region?.fiscal_year_format === 'october-september') {
    return `FY${year}`;
  }
  return `FY${year}`;
}

export function formatCurrencyValue(
  value: number | null | undefined,
  region?: RegionConfig,
  compact = false
): string {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  const symbol = region?.currency?.symbol ?? '';
  const unit = region?.currency?.unit_abbr ?? '';

  // Auto-scale: the backend occasionally returns raw currency amounts (full dollars /
  // rupees) even when the region unit is "M" (millions) or "Cr" (crores).
  // Detect this by checking whether the absolute value is implausibly large for the
  // stated unit, and divide it down to the correct scale.
  //   "M"  → 1 M = 1,000,000   – a real company's value won't exceed ~5,000,000 M
  //   "Cr" → 1 Cr = 10,000,000 – a real company's value won't exceed ~2,000,000 Cr
  let v = value;
  if (unit === 'M' && Math.abs(value) >= 1_000_000_000) {
    v = value / 1_000_000;
  } else if (unit === 'Cr' && Math.abs(value) >= 10_000_000_000) {
    v = value / 10_000_000;
  }

  if (compact) {
    if (Math.abs(v) >= 1000) {
      return `${symbol}${(v / 1000).toFixed(1)}K ${unit}`;
    }
  }
  return `${symbol}${v.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${unit}`.trim();
}

export function formatPercent(value: number | null | undefined, alreadyPct = false): string {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  const pct = alreadyPct ? value : value * 100;
  return `${pct.toFixed(1)}%`;
}

/**
 * Compact numeric formatter for dense multi-year tables.
 *
 * Returns the bare number (comma-separated, no symbol, no unit) so the unit can
 * be hoisted into the table header — keeps cells narrow when 10+ years of data
 * are displayed side-by-side. Negatives are wrapped in accounting-style
 * parentheses; callers should color them red.
 */
export function formatCompactCurrency(
  value: number | null | undefined,
  region?: RegionConfig
): string {
  if (value === null || value === undefined || isNaN(value)) return '—';
  const unit = region?.currency?.unit_abbr ?? '';

  let v = value;
  if (unit === 'M' && Math.abs(value) >= 1_000_000_000) {
    v = value / 1_000_000;
  } else if (unit === 'Cr' && Math.abs(value) >= 10_000_000_000) {
    v = value / 10_000_000;
  }

  const isNeg = v < 0;
  // Show up to 2 decimals (trailing zeros dropped) so financial-statement
  // tables (Balance Sheet, Profit & Loss, Financial Summary) don't round
  // values to whole numbers; long values are capped at 2 decimal places.
  const abs = Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return isNeg ? `(${abs})` : abs;
}

/** Human-readable unit label for the table header (e.g. "₹ in Crores"). */
export function formatUnitLabel(region?: RegionConfig): string {
  const symbol = region?.currency?.symbol ?? '';
  const unit = region?.currency?.unit_abbr ?? '';
  const unitFull =
    unit === 'Cr' ? 'Crores' :
    unit === 'M'  ? 'Millions' :
    unit === 'B'  ? 'Billions' :
    unit === 'K'  ? 'Thousands' :
    unit;
  if (!symbol && !unitFull) return '';
  return `All figures in ${symbol}${symbol ? ' ' : ''}${unitFull}`.trim();
}

/** Tooltip formatter for recharts that shows currency values */
export function currencyTooltipFormatter(region?: RegionConfig) {
  return (value: number) => [formatCurrencyValue(value, region), ''];
}

/** Tooltip formatter for recharts that shows percentage values */
export function pctTooltipFormatter(value: number) {
  return [`${(value * 100).toFixed(1)}%`, ''];
}

/**
 * Build a minimal RegionConfig from a country code or data source. Used by
 * the case-library enhancement panels (focal-company snapshot, peer table)
 * so cross-country values render with the right currency symbol and unit.
 *
 * Country codes accepted: 'US' | 'IN' | 'INDIA' | 'UK' | 'GB' (case-insensitive).
 * `source` (sec | moneycontrol | companies_house) is a fallback when no
 * country is available.
 */
export function regionFromCountry(
  country?: string | null,
  source?: string | null,
): RegionConfig | undefined {
  const c = (country ?? '').toUpperCase();
  const s = (source ?? '').toLowerCase();
  if (c === 'IN' || c === 'INDIA' || s === 'moneycontrol') {
    return {
      country: 'INDIA',
      currency: {
        code: 'INR',
        symbol: '₹',
        unit: 'Crores',
        unit_abbr: 'Cr',
        decimal_places: 2,
        locale: 'en-IN',
      },
      fiscal_year_format: 'april-march',
      data_source: 'MONEYCONTROL',
    };
  }
  if (c === 'UK' || c === 'GB' || s === 'companies_house') {
    return {
      country: 'UK',
      currency: {
        code: 'GBP',
        symbol: '£',
        unit: 'Millions',
        unit_abbr: 'M',
        decimal_places: 2,
        locale: 'en-GB',
      },
      fiscal_year_format: 'calendar',
      data_source: 'COMPANIES_HOUSE',
    };
  }
  if (c === 'US' || s === 'sec') {
    return {
      country: 'US',
      currency: {
        code: 'USD',
        symbol: '$',
        unit: 'Millions',
        unit_abbr: 'M',
        decimal_places: 2,
        locale: 'en-US',
      },
      fiscal_year_format: 'calendar',
      data_source: 'SEC_EDGAR',
    };
  }
  return undefined;
}
